import type {
  Equipment,
  EquipmentPage,
} from '../../../api/active/generated/equipment';
import type {
  PackageDetail,
  PackagePage,
  PackageRevisionWriteRequest,
  PackageSummary,
  PartialPackageRevisionWriteRequest,
} from '../../../api/active/generated/package';
import type {
  PlanDetail,
  PlanPackageMembershipReplaceRequest,
  PlanPackageMembershipReplaceResult,
  PlanReadPage,
  PlanWriteRequest,
  SolutionTree,
} from '../../../api/active/generated/plan';
import { randomUuid } from '../../../api/active/randomUuid';
import type { ActiveRequestInit } from '../../../api/active/roleViewDispatch';
import {
  ActiveApiError,
  type ActiveTransport,
} from '../../../api/active/transport';
import type { CatalogReadIndex } from '../../../quote-integration/catalogReadIndex';
import {
  type LocalCatalogSnapshotDtos,
  type LocalCatalogState,
  persistLocalCatalogState,
  readLocalCatalogState,
} from './localCatalogState';
import { localDemoImageFixture } from './localDemoCatalogImages';
import {
  getLocalDemoMediaObject,
  hydrateLocalDemoMedia,
} from './localDemoMediaStore';

export type { LocalCatalogSnapshotDtos } from './localCatalogState';

const equipmentWritableFields = [
  'name',
  'sku',
  'jkyCode',
  'category',
  'deviceAttribute',
  'procurementAttribute',
  'procurementCycle',
  'weightKg',
  'sortOrder',
  'status',
  'remark',
  'localPoint',
  'localPrice',
  'offsitePoint',
  'offsitePrice',
  'rentalPrice',
  'packageItemPrice',
  'shortRentalPrice',
  'taxInclusivePlanPrice',
  'untaxedPlanPrice',
  'retailPriceTax13',
  'dynamicCost',
  'compensationPrice',
  'images',
  'replacements',
  'customFields',
];

const packageWritableFields = [
  'package.name',
  'package.status',
  'package.sort_order',
  'package.items',
  'package.item_monthly_price',
  'package.cover_images',
  'package.lighting_images',
  'package.custom',
  'plan.packages',
];

const planWritableFields = [
  'plan.name',
  'plan.status',
  'plan.sort_order',
  'plan.packages',
  'plan.monthly_price',
  'plan.cover_images',
  'plan.custom',
];

const now = () => new Date().toISOString();
const localId = (kind: string) => `local-${kind}-${randomUuid()}`;
const nextVersion = (value: string) => (BigInt(value || '0') + 1n).toString();

const jsonResponse = (body: unknown, status = 200, etag?: string) =>
  new Response(JSON.stringify(body), {
    status,
    headers: {
      'Content-Type': 'application/json; charset=utf-8',
      ...(etag ? { ETag: etag } : {}),
      'Cache-Control': 'no-store',
    },
  });

function pageItems<T>(items: T[], query: URLSearchParams) {
  const page = Math.max(1, Number(query.get('page')) || 1);
  const pageSize = Math.max(1, Number(query.get('pageSize')) || 20);
  const start = (page - 1) * pageSize;
  return {
    items: items.slice(start, start + pageSize),
    meta: {
      page,
      pageSize,
      total: items.length,
      totalPages: Math.ceil(items.length / pageSize),
    },
  };
}

function catalogIndex(
  items: Array<{
    id: string;
    currentRevisionId: string | null;
    resourceVersion: string;
    status?: 'active' | 'inactive';
    sortOrder?: number;
  }>,
): CatalogReadIndex {
  return {
    authorizationVersion: 'local-demo-read-only',
    version: 'local-demo-v1',
    items: items.map((item) => ({
      id: item.id,
      currentRevisionId: item.currentRevisionId,
      resourceVersion: item.resourceVersion,
      status: item.status,
      sortOrder: item.sortOrder,
      representationVersion: `local-demo:${item.resourceVersion}:${item.currentRevisionId ?? 'none'}`,
    })),
  };
}

function packageSummary(detail: PackageDetail): PackageSummary {
  return {
    id: detail.id,
    code: detail.code,
    currentRevisionId: detail.currentRevisionId,
    custom: detail.revision.custom,
    name: detail.revision.name,
    packageDiscountPercent: detail.packageDiscountPercent,
    packageMonthlyTotalCents: detail.packageMonthlyTotalCents,
    rentalBasisTotalCents: detail.rentalBasisTotalCents,
    resourceVersion: detail.resourceVersion,
    revisionNo: detail.revision.revisionNo,
    revisionStatus: detail.revision.lifecycleStatus,
    sortOrder: detail.revision.sortOrder,
    status: detail.status,
    writableFields: [],
  };
}

async function equipmentFieldWritePermissions(base: ActiveTransport) {
  try {
    const result = await base.request<{
      items?: Array<{
        id: string;
        resourceType?: string;
        groupKey?: string;
        sourceKind?: string;
        securityClass?: string;
        enabled?: boolean;
      }>;
    }>('/quote-fields', { cache: 'no-store' });
    const dynamic = (result.items ?? [])
      .filter(
        (field) =>
          field.resourceType === 'quote_item' &&
          field.groupKey === 'device' &&
          field.sourceKind === 'manual' &&
          field.securityClass === 'ordinary' &&
          field.enabled,
      )
      .map((field) => `equipment.custom.${field.id}`);
    return [...equipmentWritableFields, ...dynamic];
  } catch {
    return equipmentWritableFields;
  }
}

function solutionTree(snapshot: LocalCatalogSnapshotDtos): SolutionTree {
  const packagesById = new Map(
    snapshot.packageDtos.map((item) => [item.id, item]),
  );
  const plans = snapshot.planDtos.map((detail) => ({
    plan: {
      id: detail.planId,
      currentRevisionId: detail.currentRevisionId,
      deletedAt: null,
      deletedBy: null,
      resourceVersion: detail.resourceVersion,
      status: detail.status,
      code: detail.code,
    },
    packages: (detail.packages ?? []).map((relation) => {
      const pkg = packagesById.get(relation.packageId);
      if (!pkg)
        throw new Error(`本地快照缺少方案所引用的套餐：${detail.planId}`);
      return {
        packageRevisionId: relation.packageRevisionId,
        package: {
          id: pkg.id,
          currentRevisionId: pkg.currentRevisionId,
          deletedAt: null,
          deletedBy: null,
          resourceVersion: pkg.resourceVersion,
          status: pkg.status,
          code: pkg.code,
        },
        equipmentBindings: (relation.items ?? []).map((item) => ({
          deviceId: item.deviceId,
          deviceRevisionId: item.deviceRevisionId,
          equipment: item.equipment,
          packageId: item.packageId,
          packageItemMonthlyPriceCents:
            item.packageItemMonthlyPriceCents ?? null,
          packageRelationId: item.packageRelationId,
          packageRevisionId: item.packageRevisionId,
          quantity: item.quantity,
          sortOrder: item.sortOrder,
        })),
      };
    }),
  }));
  return {
    generatedAt: 'local-demo',
    plans,
    ungroupedEquipment: snapshot.equipmentDtos,
  } as SolutionTree;
}

function equipmentHistory(
  state: LocalCatalogState,
  id: string,
  revisionId: string,
) {
  return state.equipmentRevisionDtos.find(
    (item) => item.id === id && item.currentRevisionId === revisionId,
  );
}

function equipmentSnapshot(item: Equipment) {
  const withRevisionNo = item as Equipment & { revisionNo?: number };
  return {
    deviceId: item.id,
    deviceRevisionId: item.currentRevisionId,
    revisionNo: String(withRevisionNo.revisionNo ?? 1),
    equipmentCode: item.equipmentCode,
    ...(item.name === undefined ? {} : { name: item.name }),
    skuCode: item.skuCode ?? null,
    jkyCode: item.jkyCode ?? null,
    category: item.category ?? null,
    deviceAttribute: item.deviceAttribute ?? null,
    procurementAttribute: item.procurementAttribute ?? null,
    procurementCycle: item.procurementCycle ?? null,
    weightKg: item.weightKg ?? null,
    sortOrder: item.sortOrder ?? 0,
    remark: item.remark ?? null,
    localPoint: item.localPoint ?? null,
    localPrice: item.localPrice ?? null,
    offsitePoint: item.offsitePoint ?? null,
    offsitePrice: item.offsitePrice ?? null,
    rentalPrice: item.rentalPrice ?? null,
    retailPriceTax13: item.retailPriceTax13 ?? null,
    unitPrice: item.unitPrice ?? null,
    untaxedPlanPrice: item.untaxedPlanPrice ?? null,
    taxInclusivePlanPrice: item.taxInclusivePlanPrice ?? null,
    buyoutFinalPriceTax13: item.buyoutFinalPriceTax13 ?? null,
    purchaseFinalPriceExTax13: item.purchaseFinalPriceExTax13 ?? null,
    dynamicCost: item.dynamicCost ?? null,
    compensationPrice: item.compensationPrice ?? null,
    shortRentalPrice: item.shortRentalPrice ?? null,
    packageItemPrice: item.packageItemPrice ?? null,
    customFields: item.customFields ?? [],
  };
}

async function localMediaDetail(
  objectId: string,
  purpose:
    | 'product_image'
    | 'cover_package_cover_image'
    | 'lighting_package_lighting_image'
    | 'plan_image'
    | 'plan_case_image',
  sortOrder: number,
  isPrimary: boolean,
) {
  const fixture = localDemoImageFixture(objectId);
  if (fixture) {
    if (fixture.purpose !== purpose)
      throw new Error('演示图片不能用于当前图片区域。');
    const timestamp = now();
    return {
      objectId,
      storedObjectId: objectId,
      fileName: fixture.fileName,
      mediaType: fixture.mediaType,
      byteSize: fixture.byteSize,
      isPrimary,
      sortOrder,
      previewUrl: fixture.previewUrl,
      thumbnailUrl: fixture.thumbnailUrl,
      previewExpiresAt: timestamp,
      thumbnailExpiresAt: timestamp,
      purpose,
    };
  }
  const object = await getLocalDemoMediaObject(objectId);
  if (object?.status !== 'clean' || object.purpose !== purpose)
    throw new Error('图片尚未在本地演练完成上传，或用途与图片区域不一致。');
  const previewUrl = `cpq-local-demo://${objectId}`;
  const timestamp = now();
  return {
    objectId,
    storedObjectId: objectId,
    fileName: object.fileName,
    mediaType: object.mediaType as 'image/jpeg' | 'image/png' | 'image/webp',
    byteSize: object.byteSize,
    isPrimary,
    sortOrder,
    previewUrl,
    thumbnailUrl: previewUrl,
    previewExpiresAt: timestamp,
    thumbnailExpiresAt: timestamp,
    purpose,
  };
}

async function packageDetailForRevision(
  state: LocalCatalogState,
  previous: PackageDetail | undefined,
  input: PackageRevisionWriteRequest | PartialPackageRevisionWriteRequest,
  packageId: string,
): Promise<PackageDetail> {
  const revisionId = localId('package-revision');
  const oldItems = previous?.items ?? [];
  const itemInputs =
    input.items ??
    oldItems.map((item) => ({
      deviceId: item.deviceId,
      deviceRevisionId: item.deviceRevisionId,
      quantity: item.quantity,
      sortOrder: item.sortOrder,
      replacementPoolId: item.replacementPoolId ?? null,
    }));
  const items = itemInputs.map((row) => {
    const equipment = equipmentHistory(
      state,
      row.deviceId,
      row.deviceRevisionId,
    );
    if (!equipment)
      throw new Error(`本地演练中找不到设备修订：${row.deviceId}`);
    const existing = oldItems.find(
      (item) =>
        item.deviceId === row.deviceId &&
        item.deviceRevisionId === row.deviceRevisionId &&
        item.sortOrder === row.sortOrder,
    );
    const unit = equipment.packageItemPrice ?? null;
    const quantity = row.quantity;
    const subtotal = unit === null ? null : unit * quantity;
    return {
      relationId: existing?.relationId ?? localId('package-item'),
      deviceId: row.deviceId,
      deviceRevisionId: row.deviceRevisionId,
      deviceNameSnapshot: equipment.name ?? '',
      deviceSkuSnapshot: equipment.skuCode ?? null,
      quantity,
      sortOrder: row.sortOrder,
      packageItemMonthlyPriceCents: unit,
      itemMonthlySubtotalCents: subtotal,
      replacementPoolId: row.replacementPoolId ?? null,
      equipment: equipmentSnapshot(equipment),
    };
  });
  const itemTotal = items.every(
    (item) => item.itemMonthlySubtotalCents !== null,
  )
    ? items.reduce((sum, item) => sum + (item.itemMonthlySubtotalCents ?? 0), 0)
    : null;
  const rentalBasis = items.every((item) => item.equipment.rentalPrice !== null)
    ? items.reduce(
        (sum, item) => sum + (item.equipment.rentalPrice ?? 0) * item.quantity,
        0,
      )
    : null;
  const revisionNo = (previous?.revision.revisionNo ?? 0) + 1;
  const currentRevisionId = revisionId;
  const status = input.targetPackageStatus ?? previous?.status ?? 'inactive';
  const revision = {
    id: revisionId,
    revisionNo,
    name: input.name ?? previous?.revision.name ?? '未命名套餐',
    sortOrder: input.sortOrder ?? previous?.revision.sortOrder ?? 0,
    lifecycleStatus:
      input.publish === true
        ? 'published'
        : (previous?.revision.lifecycleStatus ?? 'draft'),
    custom: input.custom ?? previous?.revision.custom ?? {},
  } as PackageDetail['revision'];
  const coverImages = input.coverImages
    ? await Promise.all(
        input.coverImages.map((image) =>
          localMediaDetail(
            image.objectId,
            'cover_package_cover_image',
            image.sortOrder,
            image.isPrimary ?? false,
          ),
        ),
      )
    : (previous?.coverImages ?? []);
  const lightingImages = input.lightingImages
    ? await Promise.all(
        input.lightingImages.map((image) =>
          localMediaDetail(
            image.objectId,
            'lighting_package_lighting_image',
            image.sortOrder,
            image.isPrimary ?? false,
          ),
        ),
      )
    : (previous?.lightingImages ?? []);
  const detail: PackageDetail = {
    id: packageId,
    code:
      previous?.code ??
      `DEMO-PKG-${String(state.packageDtos.length + 1).padStart(3, '0')}`,
    currentRevisionId,
    resourceVersion: previous ? nextVersion(previous.resourceVersion) : '1',
    status,
    revision,
    items,
    coverImages,
    lightingImages,
    packageMonthlyTotalCents: itemTotal,
    rentalBasisTotalCents: rentalBasis,
    packageDiscountPercent:
      itemTotal === null || rentalBasis === null || rentalBasis === 0
        ? null
        : (itemTotal * 100) / rentalBasis,
    planUsages: [],
    writableFields: packageWritableFields,
  };
  return detail;
}

function safeTotal(values: Array<number | null | undefined>): number | null {
  if (values.some((value) => value == null || !Number.isSafeInteger(value)))
    return null;
  const total = values.reduce<number>((sum, value) => sum + (value ?? 0), 0);
  return Number.isSafeInteger(total) ? total : null;
}

function allocateMoney(total: number, weights: readonly number[]): number[] {
  const sum = weights.reduce((value, item) => value + BigInt(item), 0n);
  if (sum === 0n) return weights.map(() => 0);
  const parts = weights.map((weight, index) => {
    const product = BigInt(total) * BigInt(weight);
    return { index, cents: product / sum, remainder: product % sum };
  });
  let remaining =
    BigInt(total) - parts.reduce((value, part) => value + part.cents, 0n);
  for (const part of parts
    .slice()
    .sort((a, b) =>
      a.remainder === b.remainder
        ? a.index - b.index
        : a.remainder > b.remainder
          ? -1
          : 1,
    )) {
    if (remaining === 0n) break;
    part.cents += 1n;
    remaining -= 1n;
  }
  return parts
    .sort((a, b) => a.index - b.index)
    .map((part) => Number(part.cents));
}

async function planDetailForRevision(
  state: LocalCatalogState,
  previous: PlanDetail | undefined,
  planId: string,
  packageRevisionIds: readonly string[],
  patch: Partial<NonNullable<PlanWriteRequest['initialRevision']>> = {},
): Promise<PlanDetail> {
  const exactPackages = packageRevisionIds.map((revisionId) => {
    const value = state.packageRevisionDtos.find(
      (item) => item.revision.id === revisionId,
    );
    if (!value) throw new Error(`本地演练中找不到套餐修订：${revisionId}`);
    return value;
  });
  const items = exactPackages.flatMap((pkg, packageOrder) =>
    (pkg.items ?? []).map((item, itemOrder) => ({
      pkg,
      item,
      packageOrder,
      itemOrder,
    })),
  );
  const baseTotal = safeTotal(
    exactPackages.map((pkg) => pkg.packageMonthlyTotalCents),
  );
  const targetTotal =
    patch.planMonthlyTotalCents ??
    previous?.planMonthlyTotalCents ??
    baseTotal ??
    0;
  const canAllocate =
    baseTotal !== null &&
    items.every((entry) => entry.item.itemMonthlySubtotalCents != null);
  const allocated = canAllocate
    ? allocateMoney(
        targetTotal,
        items.map((entry) => entry.item.itemMonthlySubtotalCents ?? 0),
      )
    : items.map(() => null);
  let itemCursor = 0;
  const packages = exactPackages.map((pkg, packageOrder) => {
    const packageItems = (pkg.items ?? []).map((item) => {
      const line = allocated[itemCursor++];
      const planUnit =
        line === null || item.quantity === 0
          ? null
          : Math.floor((line + item.quantity / 2) / item.quantity);
      return {
        packageId: pkg.id,
        packageRevisionId: pkg.revision.id,
        packageRelationId: item.relationId,
        replacementPoolId: item.replacementPoolId ?? null,
        deviceId: item.deviceId,
        deviceRevisionId: item.deviceRevisionId,
        quantity: item.quantity,
        sortOrder: item.sortOrder,
        packageItemMonthlyPriceCents: item.packageItemMonthlyPriceCents ?? null,
        packageItemMonthlySubtotalCents: item.itemMonthlySubtotalCents ?? null,
        planUnitMonthlyPriceCents: planUnit,
        lineMonthlySubtotalCents: item.itemMonthlySubtotalCents ?? null,
        planLineMonthlySubtotalCents: line,
        equipment: item.equipment,
      };
    });
    const base = safeTotal(
      packageItems.map((item) => item.packageItemMonthlySubtotalCents),
    );
    const planSum = safeTotal(
      packageItems.map((item) => item.planLineMonthlySubtotalCents),
    );
    return {
      planPackageMembershipId:
        previous?.packages?.find(
          (old) =>
            old.packageRevisionId === pkg.revision.id &&
            old.sortOrder === packageOrder,
        )?.planPackageMembershipId ?? localId('plan-package'),
      packageId: pkg.id,
      packageRevisionId: pkg.revision.id,
      packageNameSnapshot: pkg.revision.name,
      sortOrder: packageOrder,
      packageMonthlySubtotalCents: base ?? undefined,
      planPackageMonthlySubtotalCents: planSum ?? undefined,
      items: packageItems,
    };
  });
  const revisionId = localId('plan-revision');
  const revisionNo = (previous?.revision.revisionNo ?? 0) + 1;
  const discount =
    baseTotal === null || baseTotal === 0
      ? null
      : `${((targetTotal * 10_000 + Math.floor(baseTotal / 2)) / baseTotal / 100).toFixed(2)}`;
  const [images, caseImages] = await Promise.all([
    patch.images
      ? Promise.all(
          patch.images.map((image) =>
            localMediaDetail(
              image.objectId,
              'plan_image',
              image.sortOrder,
              image.isPrimary ?? false,
            ),
          ),
        )
      : (previous?.images ?? []),
    patch.caseImages
      ? Promise.all(
          patch.caseImages.map((image) =>
            localMediaDetail(
              image.objectId,
              'plan_case_image',
              image.sortOrder,
              image.isPrimary ?? false,
            ),
          ),
        )
      : (previous?.caseImages ?? []),
  ]);
  const next: PlanDetail = {
    planId,
    code:
      previous?.code ??
      `DEMO-PLAN-${String(state.planDtos.length + 1).padStart(3, '0')}`,
    currentRevisionId: revisionId,
    resourceVersion: previous ? nextVersion(previous.resourceVersion) : '1',
    revision: { id: revisionId, revisionNo },
    status: patch.status ?? previous?.status ?? 'active',
    name: patch.name ?? previous?.name ?? '未命名方案',
    sortOrder: patch.sortOrder ?? previous?.sortOrder ?? 0,
    cases: patch.cases ?? previous?.cases ?? [],
    custom: patch.custom ?? previous?.custom ?? {},
    images,
    caseImages,
    packageRevisionIds: [...packageRevisionIds],
    packages,
    packageMonthlyBaseTotalCents: baseTotal ?? undefined,
    planMonthlyTotalCents: targetTotal,
    discount,
    revisionDiscount: discount,
    incompleteReasons: canAllocate ? [] : ['套餐金额信息不完整'],
    writableFields: planWritableFields,
  };
  return next;
}

function localResponse(
  path: string,
  init: ActiveRequestInit,
  snapshot: LocalCatalogState,
  equipmentFields: readonly string[] = equipmentWritableFields,
): Response | undefined {
  const method = (init?.method ?? 'GET').toUpperCase();
  const url = new URL(path, 'http://local-demo.invalid');
  const pathname = url.pathname;

  if (pathname === '/equipment' && method === 'GET') {
    const query = url.searchParams;
    let items = [...snapshot.equipmentDtos].sort(
      (left, right) =>
        (left.sortOrder ?? 0) - (right.sortOrder ?? 0) ||
        left.id.localeCompare(right.id),
    );
    const search = query.get('search')?.trim().toLocaleLowerCase();
    if (search)
      items = items.filter((item) =>
        [item.name, item.equipmentCode, item.skuCode, item.jkyCode].some(
          (value) => value?.toLocaleLowerCase().includes(search),
        ),
      );
    for (const key of ['category', 'status'] as const) {
      const value = query.get(key);
      if (value) items = items.filter((item) => item[key] === value);
    }
    for (const [key, queryKey] of [
      ['retailPriceTax13', 'retailPriceTax13'],
      ['rentalPrice', 'rentalPrice'],
    ] as const) {
      const minimum = query.get(`${queryKey}Min`);
      const maximum = query.get(`${queryKey}Max`);
      if (minimum !== null)
        items = items.filter(
          (item) => item[key] !== null && Number(item[key]) >= Number(minimum),
        );
      if (maximum !== null)
        items = items.filter(
          (item) => item[key] !== null && Number(item[key]) <= Number(maximum),
        );
    }
    const updatedFrom = query.get('updatedFrom');
    const updatedTo = query.get('updatedTo');
    if (updatedFrom) {
      const minimum = Date.parse(updatedFrom);
      items = items.filter((item) => Date.parse(item.updatedAt) >= minimum);
    }
    if (updatedTo) {
      const maximum = Date.parse(updatedTo);
      items = items.filter((item) => Date.parse(item.updatedAt) <= maximum);
    }
    const categories = [
      ...new Set(
        items.flatMap((item) => (item.category ? [item.category] : [])),
      ),
    ];
    const result: EquipmentPage = {
      ...pageItems(items, query),
      meta: {
        ...pageItems(items, query).meta,
        facets: {
          categories,
          deviceAttributes: [
            ...new Set(
              items.flatMap((item) =>
                item.deviceAttribute ? [item.deviceAttribute] : [],
              ),
            ),
          ],
          procurementAttributes: [
            ...new Set(
              items.flatMap((item) =>
                item.procurementAttribute ? [item.procurementAttribute] : [],
              ),
            ),
          ],
          procurementCycles: [
            ...new Set(
              items.flatMap((item) =>
                item.procurementCycle ? [item.procurementCycle] : [],
              ),
            ),
          ],
        },
      },
    };
    return jsonResponse({
      ...result,
      items: result.items.map((item) => ({
        ...item,
        writableFields: [...equipmentFields],
      })),
    });
  }
  if (pathname === '/equipment/catalog-index' && method === 'GET') {
    const status = url.searchParams.get('status');
    return jsonResponse(
      catalogIndex(
        snapshot.equipmentDtos
          .map((item) => ({
            id: item.id,
            currentRevisionId: item.currentRevisionId,
            resourceVersion: item.resourceVersion,
            status: item.status,
            sortOrder: item.sortOrder,
          }))
          .filter((item) => !status || item.status === status),
      ),
    );
  }
  if (pathname === '/equipment:batch-get' && method === 'POST') {
    const body = JSON.parse(String(init?.body ?? '{}')) as {
      equipmentIds?: string[];
    };
    if (
      !body.equipmentIds ||
      body.equipmentIds.some(
        (id) => !snapshot.equipmentDtos.some((item) => item.id === id),
      )
    )
      return jsonResponse({ code: 'NOT_FOUND', title: '设备不存在' }, 404);
    return jsonResponse({
      items: body.equipmentIds.map((id) => ({
        ...snapshot.equipmentDtos.find((item) => item.id === id),
        writableFields: [...equipmentFields],
      })),
    });
  }
  const equipment = /^\/equipment\/([^/]+)$/.exec(pathname);
  if (equipment && method === 'GET') {
    const item = snapshot.equipmentDtos.find(
      (candidate) => candidate.id === decodeURIComponent(equipment[1]),
    );
    return item
      ? jsonResponse(
          {
            ...item,
            images: item.images ?? [],
            writableFields: equipmentFields,
          },
          200,
          `"${item.resourceVersion}"`,
        )
      : jsonResponse({ code: 'NOT_FOUND', title: '设备不存在' }, 404);
  }

  if (pathname === '/packages' && method === 'GET') {
    const query = url.searchParams;
    const search = query.get('search')?.trim().toLocaleLowerCase();
    let items = snapshot.packageDtos;
    const status = query.get('status');
    if (status) items = items.filter((item) => item.status === status);
    if (search)
      items = items.filter((item) => {
        const nameMatch = `${item.revision.name ?? ''} ${item.code ?? ''}`
          .toLocaleLowerCase()
          .includes(search);
        const deviceMatch = (item.items ?? []).some((row) =>
          `${row.equipment.name ?? ''} ${row.equipment.skuCode ?? ''}`
            .toLocaleLowerCase()
            .includes(search),
        );
        return query.get('searchBy') === 'device' ? deviceMatch : nameMatch;
      });
    const page = pageItems(items.map(packageSummary), query);
    const result: PackagePage = page;
    return jsonResponse({
      ...result,
      items: result.items.map((item) => ({
        ...item,
        writableFields: packageWritableFields,
      })),
    });
  }
  if (pathname === '/packages/catalog-index' && method === 'GET') {
    const status = url.searchParams.get('status');
    return jsonResponse(
      catalogIndex(
        snapshot.packageDtos
          .map((item) => ({
            id: item.id,
            currentRevisionId: item.currentRevisionId,
            resourceVersion: item.resourceVersion,
            status: item.status,
            sortOrder: item.revision.sortOrder,
          }))
          .filter((item) => !status || item.status === status),
      ),
    );
  }
  if (pathname === '/packages:batch-get' && method === 'POST') {
    const body = JSON.parse(String(init?.body ?? '{}')) as {
      packageIds?: string[];
    };
    if (
      !body.packageIds ||
      body.packageIds.some(
        (id) => !snapshot.packageDtos.some((item) => item.id === id),
      )
    )
      return jsonResponse({ code: 'NOT_FOUND', title: '套餐不存在' }, 404);
    return jsonResponse({
      items: body.packageIds.map((id) => ({
        ...snapshot.packageDtos.find((item) => item.id === id),
        writableFields: packageWritableFields,
      })),
    });
  }
  const pkg = /^\/packages\/([^/]+)$/.exec(pathname);
  if (pkg && method === 'GET') {
    const item = snapshot.packageDtos.find(
      (candidate) => candidate.id === decodeURIComponent(pkg[1]),
    );
    return item
      ? jsonResponse(
          { ...item, writableFields: packageWritableFields },
          200,
          `"${item.resourceVersion}"`,
        )
      : jsonResponse({ code: 'NOT_FOUND', title: '套餐不存在' }, 404);
  }

  if (pathname === '/plans' && method === 'GET') {
    const search = url.searchParams.get('search')?.trim().toLocaleLowerCase();
    let items = snapshot.planDtos;
    const status = url.searchParams.get('status');
    if (status) items = items.filter((item) => item.status === status);
    if (search)
      items = items.filter((item) => {
        const planText =
          `${item.name ?? ''} ${item.code ?? ''}`.toLocaleLowerCase();
        const packageText = (item.packages ?? [])
          .map((pkg) => pkg.packageNameSnapshot ?? '')
          .join(' ')
          .toLocaleLowerCase();
        return url.searchParams.get('searchBy') === 'package'
          ? packageText.includes(search)
          : planText.includes(search);
      });
    const page = pageItems(items, url.searchParams) as PlanReadPage;
    return jsonResponse({
      ...page,
      items: page.items.map((item) => ({
        ...item,
        writableFields: planWritableFields,
      })),
    });
  }
  if (pathname === '/plans/catalog-index' && method === 'GET') {
    const status = url.searchParams.get('status');
    return jsonResponse(
      catalogIndex(
        snapshot.planDtos
          .map((item) => ({
            id: item.planId,
            currentRevisionId: item.currentRevisionId,
            resourceVersion: item.resourceVersion,
            status: item.status,
            sortOrder: item.sortOrder,
          }))
          .filter((item) => !status || item.status === status),
      ),
    );
  }
  if (pathname === '/solution-tree' && method === 'GET')
    return jsonResponse(solutionTree(snapshot));
  const plan = /^\/plans\/([^/]+)$/.exec(pathname);
  if (plan && method === 'GET') {
    const item = snapshot.planDtos.find(
      (candidate) => candidate.planId === decodeURIComponent(plan[1]),
    );
    return item
      ? jsonResponse(
          { ...item, writableFields: planWritableFields },
          200,
          `"${item.resourceVersion}"`,
        )
      : jsonResponse({ code: 'NOT_FOUND', title: '方案不存在' }, 404);
  }
  return undefined;
}

function localProblem(
  status: number,
  title: string,
  code = status === 409 ? 'RESOURCE_VERSION_CONFLICT' : 'LOCAL_DEMO_INVALID',
): Response {
  return jsonResponse({ code, title, detail: title }, status);
}

async function hydrateResponse(response: Response): Promise<Response> {
  if (
    response.status === 204 ||
    !response.headers.get('Content-Type')?.includes('application/json')
  )
    return response;
  const body = await response.json();
  let hydrated = body;
  try {
    hydrated = await hydrateLocalDemoMedia(body);
  } catch {
    // A preview lookup failure must not turn a committed local rehearsal into a failed save.
  }
  return jsonResponse(
    hydrated,
    response.status,
    response.headers.get('ETag') ?? undefined,
  );
}

function expectedVersionMatches(
  init: ActiveRequestInit,
  resourceVersion: string,
): boolean {
  const supplied = new Headers(init.headers).get('If-Match');
  return supplied === `"${resourceVersion}"`;
}

function commitState(current: LocalCatalogState, next: LocalCatalogState) {
  persistLocalCatalogState(next);
  current.equipmentDtos.splice(
    0,
    current.equipmentDtos.length,
    ...next.equipmentDtos,
  );
  current.packageDtos.splice(
    0,
    current.packageDtos.length,
    ...next.packageDtos,
  );
  current.planDtos.splice(0, current.planDtos.length, ...next.planDtos);
  current.equipmentRevisionDtos.splice(
    0,
    current.equipmentRevisionDtos.length,
    ...next.equipmentRevisionDtos,
  );
  current.packageRevisionDtos.splice(
    0,
    current.packageRevisionDtos.length,
    ...next.packageRevisionDtos,
  );
  current.planRevisionDtos.splice(
    0,
    current.planRevisionDtos.length,
    ...next.planRevisionDtos,
  );
}

function cloneCatalogState(state: LocalCatalogState): LocalCatalogState {
  return structuredClone(state);
}

function packageReferenced(state: LocalCatalogState, packageId: string) {
  return state.planDtos.some((plan) =>
    plan.packages?.some((pkg) => pkg.packageId === packageId),
  );
}

function deviceReferenced(state: LocalCatalogState, deviceId: string) {
  return (
    state.packageDtos.some((pkg) =>
      pkg.items?.some((item) => item.deviceId === deviceId),
    ) ||
    state.planDtos.some((plan) =>
      plan.packages?.some((pkg) =>
        pkg.items.some((item) => item.deviceId === deviceId),
      ),
    )
  );
}

function refreshPlanUsages(state: LocalCatalogState) {
  for (const pkg of state.packageDtos) {
    pkg.planUsages = state.planDtos.flatMap((plan) =>
      (plan.packages ?? [])
        .filter(
          (membership) =>
            membership.packageRevisionId === pkg.currentRevisionId,
        )
        .map((membership) => ({
          planId: plan.planId,
          planRevisionId: plan.currentRevisionId,
          planNameSnapshot: plan.name,
          sortOrder: membership.sortOrder,
        })),
    );
  }
}

async function applyPlanMembershipUpdates(
  state: LocalCatalogState,
  items: PlanPackageMembershipReplaceRequest['items'],
): Promise<PlanPackageMembershipReplaceResult['items']> {
  const seen = new Set<string>();
  const result: PlanPackageMembershipReplaceResult['items'] = [];
  for (const input of items) {
    if (seen.has(input.planId)) throw new Error('同一批次不能重复提交方案。');
    seen.add(input.planId);
    const previous = state.planDtos.find(
      (item) => item.planId === input.planId,
    );
    if (!previous) throw new Error(`本地演练中找不到方案：${input.planId}`);
    if (previous.resourceVersion !== input.expectedResourceVersion)
      throw Object.assign(new Error('方案版本已变化，请重新打开后保存。'), {
        status: 409,
      });
    const revisions =
      input.packageRevisionIds ?? previous.packageRevisionIds ?? [];
    const updated = await planDetailForRevision(
      state,
      previous,
      previous.planId,
      revisions,
      input.revisionPatch ?? {},
    );
    state.planDtos[
      state.planDtos.findIndex((item) => item.planId === input.planId)
    ] = updated;
    state.planRevisionDtos.push(structuredClone(updated));
    result.push({
      planId: updated.planId,
      oldResourceVersion: previous.resourceVersion,
      newResourceVersion: updated.resourceVersion,
      newPlanRevisionId: updated.currentRevisionId,
      packageRevisionIds: [...revisions],
    });
  }
  return result;
}

async function localMutation(
  path: string,
  init: ActiveRequestInit,
  current: LocalCatalogState,
  equipmentFields: readonly string[] = equipmentWritableFields,
): Promise<Response | undefined> {
  const method = (init.method ?? 'GET').toUpperCase();
  const url = new URL(path, 'http://local-demo.invalid');
  const { pathname } = url;
  const body = (() => {
    try {
      return JSON.parse(String(init.body ?? '{}')) as Record<string, any>;
    } catch {
      return null;
    }
  })();

  if (pathname === '/equipment' && method === 'POST') {
    if (!body?.name || !body.status)
      return localProblem(400, '请填写设备名称和状态。');
    try {
      const state = cloneCatalogState(current);
      const id = localId('equipment');
      const currentRevisionId = localId('equipment-revision');
      const timestamp = now();
      const value: Equipment = {
        id,
        equipmentCode: `DEMO-EQ-${String(state.equipmentDtos.length + 1).padStart(4, '0')}`,
        currentRevisionId,
        resourceVersion: '1',
        rowVersion: '1',
        ownerUserId: 'offline-demo-user',
        departmentId: 'offline-demo-department',
        createdAt: timestamp,
        updatedAt: timestamp,
        deletedAt: null,
        deletedBy: null,
        name: body.name,
        skuCode: body.sku ?? null,
        jkyCode: body.jkyCode ?? null,
        category: body.category ?? null,
        deviceAttribute: body.deviceAttribute ?? null,
        procurementAttribute: body.procurementAttribute ?? null,
        procurementCycle: body.procurementCycle ?? null,
        weightKg: body.weightKg ?? null,
        sortOrder: body.sortOrder ?? 0,
        status: body.status,
        remark: body.remark ?? null,
        localPoint: body.localPoint ?? null,
        localPrice: body.localPrice ?? null,
        offsitePoint: body.offsitePoint ?? null,
        offsitePrice: body.offsitePrice ?? null,
        rentalPrice: body.rentalPrice ?? null,
        packageItemPrice: body.packageItemPrice ?? null,
        shortRentalPrice: body.shortRentalPrice ?? null,
        taxInclusivePlanPrice: body.taxInclusivePlanPrice ?? null,
        unitPrice: body.unitPrice ?? null,
        untaxedPlanPrice: body.untaxedPlanPrice ?? null,
        retailPriceTax13: body.retailPriceTax13 ?? null,
        dynamicCost: body.dynamicCost ?? null,
        compensationPrice: body.compensationPrice ?? null,
        customFields: body.customFields ?? [],
        images: [],
        replacements: [],
        writableFields: [...equipmentFields],
        revisionNo: 1,
      } as Equipment;
      value.images = await Promise.all(
        (body.images ?? []).map(async (image: any) => {
          const media = await localMediaDetail(
            String(image.storedObjectId),
            'product_image',
            image.sortOrder,
            image.isPrimary,
          );
          return {
            storedObjectId: media.objectId,
            mediaType: media.mediaType,
            byteSize: media.byteSize,
            isPrimary: media.isPrimary,
            sortOrder: media.sortOrder,
            previewUrl: media.previewUrl,
            thumbnailUrl: media.thumbnailUrl,
            previewExpiresAt: media.previewExpiresAt,
            thumbnailExpiresAt: media.thumbnailExpiresAt,
          };
        }),
      );
      value.replacements = body.replacements ?? [];
      state.equipmentDtos.push(value);
      state.equipmentRevisionDtos.push(structuredClone(value));
      commitState(current, state);
      return jsonResponse(value, 201, '"1"');
    } catch (error) {
      return localProblem(
        400,
        error instanceof Error ? error.message : '设备创建失败',
      );
    }
  }

  const equipmentMatch = /^\/equipment\/([^/]+)$/.exec(pathname);
  if (equipmentMatch && method === 'PATCH') {
    const id = decodeURIComponent(equipmentMatch[1]);
    const existing = current.equipmentDtos.find((item) => item.id === id);
    if (!existing) return localProblem(404, '设备不存在。', 'NOT_FOUND');
    if (!expectedVersionMatches(init, existing.resourceVersion))
      return localProblem(
        409,
        '设备版本已变化，请刷新后重试。',
        'RESOURCE_VERSION_CONFLICT',
      );
    if (!body) return localProblem(400, '设备数据格式无效。');
    const state = cloneCatalogState(current);
    const old = state.equipmentDtos.find((item) => item.id === id);
    if (!old) return localProblem(404, '设备不存在。', 'NOT_FOUND');
    const previousRevisionNo = Number(
      (old as Equipment & { revisionNo?: number }).revisionNo ?? 1,
    );
    const updated: Equipment = {
      ...old,
      ...body,
      skuCode: body.sku === undefined ? old.skuCode : body.sku,
      currentRevisionId: localId('equipment-revision'),
      resourceVersion: nextVersion(old.resourceVersion),
      rowVersion: nextVersion(old.rowVersion),
      updatedAt: now(),
      writableFields: [...equipmentFields],
      revisionNo: previousRevisionNo + 1,
    } as Equipment;
    if (body.images !== undefined) {
      try {
        updated.images = await Promise.all(
          body.images.map(async (image: any) => {
            const media = await localMediaDetail(
              String(image.storedObjectId),
              'product_image',
              image.sortOrder,
              image.isPrimary,
            );
            return {
              storedObjectId: media.objectId,
              mediaType: media.mediaType,
              byteSize: media.byteSize,
              isPrimary: media.isPrimary,
              sortOrder: media.sortOrder,
              previewUrl: media.previewUrl,
              thumbnailUrl: media.thumbnailUrl,
              previewExpiresAt: media.previewExpiresAt,
              thumbnailExpiresAt: media.thumbnailExpiresAt,
            };
          }),
        );
      } catch (error) {
        return localProblem(
          422,
          error instanceof Error ? error.message : '设备图片无效',
        );
      }
    }
    const index = state.equipmentDtos.findIndex((item) => item.id === id);
    state.equipmentDtos[index] = updated;
    state.equipmentRevisionDtos.push(structuredClone(updated));
    commitState(current, state);
    return jsonResponse(updated, 200, `"${updated.resourceVersion}"`);
  }

  if (equipmentMatch && method === 'DELETE') {
    const id = decodeURIComponent(equipmentMatch[1]);
    const existing = current.equipmentDtos.find((item) => item.id === id);
    if (!existing) return localProblem(404, '设备不存在。', 'NOT_FOUND');
    if (!expectedVersionMatches(init, existing.resourceVersion))
      return localProblem(
        409,
        '设备版本已变化，请刷新后重试。',
        'RESOURCE_VERSION_CONFLICT',
      );
    if (deviceReferenced(current, id))
      return localProblem(
        409,
        '该设备仍被套餐或方案引用，暂不能删除。',
        'CATALOG_ITEM_IN_USE',
      );
    const state = cloneCatalogState(current);
    state.equipmentDtos = state.equipmentDtos.filter((item) => item.id !== id);
    commitState(current, state);
    return new Response(null, { status: 204 });
  }

  if (pathname === '/packages' && method === 'POST') {
    if (!body?.initialRevision)
      return localProblem(400, '套餐首个版本内容缺失。');
    try {
      const state = cloneCatalogState(current);
      const id = localId('package');
      const detail = await packageDetailForRevision(
        state,
        undefined,
        body.initialRevision,
        id,
      );
      state.packageDtos.push(detail);
      state.packageRevisionDtos.push(structuredClone(detail));
      refreshPlanUsages(state);
      commitState(current, state);
      return jsonResponse(detail, 201, `"${detail.resourceVersion}"`);
    } catch (error) {
      return localProblem(
        400,
        error instanceof Error ? error.message : '套餐创建失败',
      );
    }
  }

  const packageRevisionMatch = /^\/packages\/([^/]+)\/revisions$/.exec(
    pathname,
  );
  if (packageRevisionMatch && method === 'POST') {
    const id = decodeURIComponent(packageRevisionMatch[1]);
    const existing = current.packageDtos.find((item) => item.id === id);
    if (!existing) return localProblem(404, '套餐不存在。', 'NOT_FOUND');
    if (!expectedVersionMatches(init, existing.resourceVersion))
      return localProblem(
        409,
        '套餐版本已变化，请刷新后重试。',
        'RESOURCE_VERSION_CONFLICT',
      );
    if (!body) return localProblem(400, '套餐数据格式无效。');
    try {
      const state = cloneCatalogState(current);
      const previous = state.packageDtos.find((item) => item.id === id);
      if (!previous) return localProblem(404, '套餐不存在。', 'NOT_FOUND');
      const detail = await packageDetailForRevision(state, previous, body, id);
      state.packageRevisionDtos.push(structuredClone(detail));
      state.packageDtos[state.packageDtos.findIndex((item) => item.id === id)] =
        detail;
      if (Array.isArray(body.planMemberships?.items)) {
        await applyPlanMembershipUpdates(state, body.planMemberships.items);
      }
      refreshPlanUsages(state);
      commitState(current, state);
      return jsonResponse(detail, 200, `"${detail.resourceVersion}"`);
    } catch (error) {
      const status =
        typeof error === 'object' && error !== null && 'status' in error
          ? Number((error as { status?: unknown }).status) || 400
          : 400;
      return localProblem(
        status,
        error instanceof Error ? error.message : '套餐保存失败',
      );
    }
  }

  const packageMatch = /^\/packages\/([^/]+)$/.exec(pathname);
  if (packageMatch && method === 'DELETE') {
    const id = decodeURIComponent(packageMatch[1]);
    const existing = current.packageDtos.find((item) => item.id === id);
    if (!existing) return localProblem(404, '套餐不存在。', 'NOT_FOUND');
    if (!expectedVersionMatches(init, existing.resourceVersion))
      return localProblem(
        409,
        '套餐版本已变化，请刷新后重试。',
        'RESOURCE_VERSION_CONFLICT',
      );
    if (packageReferenced(current, id))
      return localProblem(
        409,
        '该套餐仍被方案引用，暂不能删除。',
        'CATALOG_ITEM_IN_USE',
      );
    const state = cloneCatalogState(current);
    state.packageDtos = state.packageDtos.filter((item) => item.id !== id);
    refreshPlanUsages(state);
    commitState(current, state);
    return new Response(null, { status: 204 });
  }

  if (pathname === '/plans' && method === 'POST') {
    if (!body?.initialRevision)
      return localProblem(400, '方案首个版本内容缺失。');
    const initial = body.initialRevision as PlanWriteRequest['initialRevision'];
    try {
      const state = cloneCatalogState(current);
      const id = localId('plan');
      const plan = await planDetailForRevision(
        state,
        undefined,
        id,
        initial.packageRevisionIds,
        initial,
      );
      state.planDtos.push(plan);
      state.planRevisionDtos.push(structuredClone(plan));
      refreshPlanUsages(state);
      commitState(current, state);
      return jsonResponse(plan, 201, `"${plan.resourceVersion}"`);
    } catch (error) {
      return localProblem(
        400,
        error instanceof Error ? error.message : '方案创建失败',
      );
    }
  }

  if (pathname === '/plan-package-memberships:replace' && method === 'POST') {
    if (!body || !Array.isArray(body.items))
      return localProblem(400, '方案套餐关联数据格式无效。');
    const inputItems =
      body.items as PlanPackageMembershipReplaceRequest['items'];
    const state = cloneCatalogState(current);
    try {
      const resultItems = await applyPlanMembershipUpdates(state, inputItems);
      refreshPlanUsages(state);
      commitState(current, state);
      return jsonResponse({
        items: resultItems,
        operationId: localId('operation'),
      });
    } catch (error) {
      const status =
        typeof error === 'object' && error !== null && 'status' in error
          ? Number((error as { status?: unknown }).status) || 400
          : 400;
      return localProblem(
        status,
        error instanceof Error ? error.message : '方案关联保存失败',
      );
    }
  }

  const planMatch = /^\/plans\/([^/]+)$/.exec(pathname);
  if (planMatch && method === 'DELETE') {
    const id = decodeURIComponent(planMatch[1]);
    const existing = current.planDtos.find((item) => item.planId === id);
    if (!existing) return localProblem(404, '方案不存在。', 'NOT_FOUND');
    if (!expectedVersionMatches(init, existing.resourceVersion))
      return localProblem(
        409,
        '方案版本已变化，请刷新后重试。',
        'RESOURCE_VERSION_CONFLICT',
      );
    const state = cloneCatalogState(current);
    state.planDtos = state.planDtos.filter((item) => item.planId !== id);
    refreshPlanUsages(state);
    commitState(current, state);
    return new Response(null, { status: 204 });
  }

  return undefined;
}

export function createLocalCatalogTransport(
  base: ActiveTransport,
  snapshot: LocalCatalogSnapshotDtos,
): ActiveTransport {
  const candidate = snapshot as Partial<LocalCatalogState>;
  const state: LocalCatalogState =
    Array.isArray(candidate.equipmentRevisionDtos) &&
    Array.isArray(candidate.packageRevisionDtos) &&
    Array.isArray(candidate.planRevisionDtos)
      ? (snapshot as LocalCatalogState)
      : readLocalCatalogState(snapshot);
  const requestResponse = async (
    path: string,
    init: ActiveRequestInit = {},
  ): Promise<Response> => {
    const method = (init?.method ?? 'GET').toUpperCase();
    const pathname = new URL(path, 'http://local-demo.invalid').pathname;
    const equipmentFields = /^\/equipment(?:\/|:|$)/.test(pathname)
      ? await equipmentFieldWritePermissions(base)
      : undefined;
    if (method === 'GET' || method === 'POST') {
      const response = localResponse(
        path,
        init,
        state,
        equipmentFields ?? equipmentWritableFields,
      );
      if (response) return hydrateResponse(response);
    }
    if (method !== 'GET') {
      const response = await localMutation(
        path,
        init,
        state,
        equipmentFields ?? equipmentWritableFields,
      );
      if (response) return hydrateResponse(response);
    }
    // The base offline transport always fails closed before fetch for all other paths/writes.
    return base.requestResponse(path, init);
  };
  return {
    ...base,
    requestResponse,
    async request<T>(path: string, init: ActiveRequestInit = {}): Promise<T> {
      const response = await requestResponse(path, init);
      if (!response.ok) {
        const problem = await response.json().catch(() => ({}));
        throw new ActiveApiError(
          response.status,
          typeof problem?.title === 'string'
            ? problem.title
            : `本地目录读取失败（HTTP ${response.status}）`,
          typeof problem?.code === 'string' ? problem.code : undefined,
          typeof problem?.title === 'string' ? problem.title : undefined,
        );
      }
      if (response.status === 204) return undefined as T;
      return (await response.json()) as T;
    },
  };
}
